import React from "react";

import { useLocation } from "react-router-dom";
import { isMobile } from "react-device-detect";

import { createAppTheme } from "./styles";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import Box from "@mui/material/Box";

import MainPage from "./general/main/MainPage";
import PageLoader from "./helper-components/pages/PageLoader";
import ConfirmationDialog from "./helper-components/input/ConfirmationDialog";

import { getKeyFromQuery } from "./helper-functions/shared/pageUtils.js";
import { disableConsoleLogging } from "./helper-functions/shared/miscUtils.js";

import { useLocalStorage } from "./helper-functions/hooks/useLocalStorage";
import { useWindowDimensions } from "./helper-functions/hooks/useWindowDimensions";
import { useSnackbar } from "./helper-functions/hooks/useSnackbar";
import { useDialog } from "./helper-functions/hooks/useDialog";
import { useNavigateWithPrev } from "./helper-functions/hooks/useNavigation.js";

import { LanguageContext, applyLanguageCode, getLanguage } from "./config/language/language";
import { DEFAULT_GUEST_PREFS } from "./helper-functions/shared/guestPrefs";
import * as Constants from "./config/constants/Constants.js";

export const ColorModeContext = React.createContext({
    toggleColorMode: () => {},
});

export default function App() {
    const debug = getKeyFromQuery("debug");
    const [mode, setMode] = useLocalStorage("colorMode", "light");
    const [language, setLanguage] = useLocalStorage("uiLanguage", "en");

    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
            },
        }),
        [setMode]
    );

    const languageApi = React.useMemo(
        () => ({
            language,
            setLanguage: (code) => setLanguage(code),
        }),
        [language, setLanguage]
    );

    React.useEffect(() => {
        applyLanguageCode(language);
    }, [language]);

    const theme = React.useMemo(() => createAppTheme(mode, isMobile), [mode]);

    console.log(`==== ${getLanguage().WEBSITE_NAME} ====`);
    console.log(`Version: ${Constants.PAGE_VERSION}`);

    if (!Constants.DEVELOPMENT_ENV && debug !== "true") disableConsoleLogging();

    return (
        <LanguageContext.Provider value={languageApi}>
            <ColorModeContext.Provider value={colorMode}>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <StyledApp />
                </ThemeProvider>
            </ColorModeContext.Provider>
        </LanguageContext.Provider>
    );
}

function StyledApp() {
    const location = useLocation();
    const { setLanguage } = React.useContext(LanguageContext);

    const [isPageLoading, setIsPageLoading] = React.useState(Constants.PAGE_ENABLE_LOADER);
    const [isConnectionToServerEstablished, setIsConnectionToServerEstablished] = React.useState(false);
    const [isLoggedIn, setIsLoggedIn] = React.useState(false);
    const [showMainPageComponents, setShowMainPageComponents] = React.useState(true);

    const [userJWT, setUserJWT] = useLocalStorage("jwt", "");
    const [userRole, setUserRole] = useLocalStorage("userRole", "");
    const [authStatus, setAuthStatus] = React.useState(null);
    const [menuData, setMenuData] = React.useState(null);
    const [menuError, setMenuError] = React.useState(null);

    const [chatUserId, setChatUserId] = useLocalStorage("chatUserId", "");
    const [chatHistory, setChatHistory] = useLocalStorage("chatHistory", []);
    const [guestPrefs, setGuestPrefs] = useLocalStorage("guestPrefs", DEFAULT_GUEST_PREFS);
    const [favorites, setFavorites] = useLocalStorage("favorites", []);

    React.useEffect(() => {
        if (!chatUserId) {
            const id = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `user-${Date.now()}`;
            setChatUserId(id);
        }
    }, [chatUserId, setChatUserId]);

    React.useEffect(() => {
        if (guestPrefs?.language) {
            setLanguage(guestPrefs.language);
            applyLanguageCode(guestPrefs.language);
        }
    }, [guestPrefs?.language, setLanguage]);

    // Venue change must reload Sheets-scoped menu (curated + ready flags).
    React.useEffect(() => {
        setMenuData(null);
        setMenuError(null);
    }, [guestPrefs?.venue]);

    const { showSnackbar } = useSnackbar();
    const { navigate } = useNavigateWithPrev();
    const { enqueueDialog, setOpen, ...options } = useDialog();

    const [isAdmin, setIsAdmin] = React.useState(false);

    const { screenHeight, screenWidth } = useWindowDimensions();
    const [openAppDrawer, setOpenAppDrawer] = React.useState(screenWidth > Constants.AUTO_OPEN_SIDEBAR_PX);
    const toggleAppDrawer = () => setOpenAppDrawer((v) => !v);

    React.useEffect(() => {
        const timer = setTimeout(() => setIsPageLoading(false), Constants.PAGE_LOADING_MS);
        return () => clearTimeout(timer);
    }, []);

    React.useEffect(() => {
        setIsAdmin(userRole === "admin");
        setIsLoggedIn(Boolean(userJWT));
    }, [userJWT, userRole]);

    return (
        <Box sx={{ bgcolor: "background.default", color: "text.primary" }}>
            <PageLoader imageSrc="/images/logo.png" show={isPageLoading} />

            <div hidden={isPageLoading}>
                <ConfirmationDialog
                    options={{
                        enqueueDialog,
                        setOpen,
                        ...options,
                    }}
                />

                <MainPage
                    menu={{
                        menuData,
                        setMenuData,
                        menuError,
                        setMenuError,
                    }}
                    chat={{
                        chatUserId,
                        chatHistory,
                        setChatHistory,
                    }}
                    auth={{
                        authStatus,
                        setAuthStatus,
                    }}
                    user={{
                        userJWT,
                        setUserJWT,
                        userRole,
                        setUserRole,
                        isLoggedIn,
                        setIsLoggedIn,
                    }}
                    roles={{
                        isAdmin,
                        setIsAdmin,
                    }}
                    prefs={{
                        guestPrefs,
                        setGuestPrefs,
                    }}
                    favorites={{
                        favorites,
                        setFavorites,
                    }}
                    browser={{
                        navigate,
                        location,
                        screenHeight,
                        screenWidth,
                        isMobile,
                        isPageLoading,
                        setIsPageLoading,
                        showMainPageComponents,
                        setShowMainPageComponents,
                    }}
                    server={{
                        isConnectionToServerEstablished,
                        setIsConnectionToServerEstablished,
                    }}
                    showSnackbar={showSnackbar}
                    showDialog={enqueueDialog}
                    setOpenDialog={setOpen}
                    sidebar={{
                        openAppDrawer,
                        toggleAppDrawer,
                        openNotificationDrawer: false,
                        toggleNotificationDrawer: () => {},
                    }}
                />
            </div>
        </Box>
    );
}
